import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestCommercialUpgrade } from '../../api/ops';
import { artavaBrand, buildMailto, getArtavaCopy } from '../../branding/artavaBrand';
import ArtavaCTA from './ArtavaCTA';
import styles from './ArtavaSupportPage.module.css';

export default function ArtavaSupportPage({ currentUser = null, publicMode = false }) {
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.startsWith('it');
  const copy = getArtavaCopy(isItalian);
  const [requestNote, setRequestNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const capabilityRows = useMemo(() => (
    isItalian
      ? [
          'Applicazioni industriali',
          'Piattaforme dati ingegneristiche',
          'Software di test e validazione',
          'Dashboard di produzione',
          'Sistemi embedded e connessi',
          'Software workflow custom',
          'Analisi dati e reporting',
          'Integrazioni di automazione industriale',
        ]
      : artavaBrand.capabilities
  ), [isItalian]);

  const submitTechnicalRequest = async () => {
    if (!requestNote.trim()) {
      setError(isItalian ? 'Aggiungi un breve contesto tecnico prima di inviare la richiesta.' : 'Add a short technical context before sending the request.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await requestCommercialUpgrade({
        desiredPlan: 'production',
        note: `[Artava technical assessment]\n${requestNote.trim()}`,
      });
      setMessage(copy.requestSaved);
      setRequestNote('');
    } catch (submitError) {
      const raw = String(submitError?.message || '').toLowerCase();
      setError(raw.includes('already pending') ? copy.requestPending : (submitError.message || (isItalian ? 'Impossibile inviare la richiesta ad Artava.' : 'Could not send the Artava request.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.hero}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>{publicMode ? artavaBrand.companyName : copy.supportTitle}</p>
            <h1 className={styles.title}>{artavaBrand.productName} by {artavaBrand.companyName}</h1>
            <p className={styles.description}>{copy.supportBody}</p>
            <div className={styles.heroLinks}>
              {!publicMode ? <Link to="/dashboard" className={styles.backLink}>{isItalian ? 'Torna a Smarti' : 'Back to Smarti'}</Link> : null}
              <a href={artavaBrand.websiteUrl} target="_blank" rel="noreferrer" className={styles.externalLink}>{artavaBrand.websiteHost}</a>
            </div>
          </div>
          <div className={styles.brandCard}>
            <img src={artavaBrand.logoSrc} alt={artavaBrand.companyName} className={styles.logo} />
            <strong>{artavaBrand.companyName}</strong>
            <p>{artavaBrand.tagline}</p>
            <span>{copy.basedIn}</span>
          </div>
        </header>

        <section className={styles.grid}>
          <article className={styles.card}>
            <p className={styles.cardEyebrow}>{copy.whoWeAreTitle}</p>
            <h2>{artavaBrand.shortDescription}</h2>
            <p>{copy.whoWeAreBody}</p>
          </article>

          <article className={styles.card}>
            <p className={styles.cardEyebrow}>{copy.whatWeBuildTitle}</p>
            <h2>{copy.capabilityHeading}</h2>
            <div className={styles.capabilityGrid}>
              {capabilityRows.map((capability) => (
                <span key={capability} className={styles.capabilityChip}>{capability}</span>
              ))}
            </div>
          </article>

          <article className={styles.card}>
            <p className={styles.cardEyebrow}>{copy.contactTitle}</p>
            <h2>{isItalian ? 'Canali diretti' : 'Direct channels'}</h2>
            <div className={styles.contactList}>
              <a href={buildMailto(`${artavaBrand.productName} support request`)}>{copy.emailLabel}: {artavaBrand.contactEmail}</a>
              <a href={artavaBrand.websiteUrl} target="_blank" rel="noreferrer">{copy.websiteLabel}: {artavaBrand.websiteHost}</a>
              <a href={artavaBrand.linkedInUrl} target="_blank" rel="noreferrer">{copy.linkedInLabel}</a>
            </div>
          </article>
        </section>

        <ArtavaCTA />

        {!publicMode && currentUser ? (
          <section className={styles.requestCard}>
            <div>
              <p className={styles.cardEyebrow}>{copy.primaryCta}</p>
              <h2>{isItalian ? 'Apri un contatto tecnico da Smarti' : 'Start a technical conversation from Smarti'}</h2>
              <p>{isItalian ? 'Questa richiesta resta nel workspace e aiuta Artava a capire il contesto tecnico prima del follow-up.' : 'This request stays inside the workspace and helps Artava understand the technical context before follow-up.'}</p>
            </div>
            {message ? <div className={`${styles.notice} ${styles.success}`}>{message}</div> : null}
            {error ? <div className={`${styles.notice} ${styles.error}`}>{error}</div> : null}
            <label className={styles.field}>
              <span>{copy.requestLabel}</span>
              <textarea
                rows={5}
                value={requestNote}
                onChange={(event) => setRequestNote(event.target.value)}
                placeholder={copy.requestPlaceholder}
              />
            </label>
            <div className={styles.requestActions}>
              <button type="button" className={styles.primaryButton} onClick={submitTechnicalRequest} disabled={saving}>
                {saving ? (isItalian ? 'Invio...' : 'Sending...') : copy.requestButton}
              </button>
              <a
                href={buildMailto('Smarti technical assessment', requestNote)}
                className={styles.secondaryButton}
              >
                {copy.emailLabel}
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
